/**
 * The Treasury module's route table (C4).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `TREASURY_ROUTES` in — which is why the wiring
 * packet's edit there is one import and one spread and contains no judgement
 * about a module it did not build.
 *
 * Two orderings are load-bearing, and both are the trick AR and bankrec already
 * use: a literal segment (`current`, `preview`, `new`) is declared **before** its
 * `:id(\d+)` sibling, so it can never be parsed as a document id. The `\d+`
 * constraint makes it safe anyway; the ordering makes it obvious.
 *
 * **The position board serves two routes on purpose.** `/treasury/position` is
 * the LIVE preview and `/treasury/positions/:id` is a PUBLISHED run, and the
 * `published` flag is the whole contract between them (§6.3): a screen that
 * showed them identically would let a treasurer quote a live figure as a
 * published one. One component, two routes, a banner that differs, and no
 * version rail on the preview.
 *
 * `TREASURY_PATHS` is the same set as strings, for links inside the module.
 * Links *out* of it — the journal behind a posted accrual, the ledger behind a
 * balance — are built and then checked against the router by `linkIfRoutable`
 * before being rendered: GL owns those URLs, and a guessed link that 404s in the
 * middle of an auditor's evidence chain is worse than plain text.
 */

import type { Router, RouteRecordRaw } from 'vue-router'

import { TREASURY_PERMISSIONS } from '@/modules/treasury/permissions'

export const TREASURY_ROUTES: RouteRecordRaw[] = [
  {
    path: '/treasury/position',
    name: 'treasury-position-preview',
    component: () => import('@/modules/treasury/position/PositionBoardView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.positionPreview',
      permission: TREASURY_PERMISSIONS.positionRead,
    },
  },
  {
    path: '/treasury/positions',
    name: 'treasury-positions',
    component: () => import('@/modules/treasury/position/PositionsListView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.positions',
      permission: TREASURY_PERMISSIONS.positionRead,
    },
  },
  {
    path: '/treasury/positions/:id(\\d+)',
    name: 'treasury-position',
    component: () => import('@/modules/treasury/position/PositionBoardView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.position',
      permission: TREASURY_PERMISSIONS.positionRead,
    },
  },
  {
    path: '/treasury/positions/:id(\\d+)/diff',
    name: 'treasury-position-diff',
    component: () => import('@/modules/treasury/position/PositionDiffView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.positionDiff',
      permission: TREASURY_PERMISSIONS.positionRead,
    },
  },

  {
    path: '/treasury/beneficiaries',
    name: 'treasury-beneficiaries',
    component: () => import('@/modules/treasury/beneficiaries/BeneficiariesListView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.beneficiaries',
      permission: TREASURY_PERMISSIONS.beneficiaryRead,
    },
  },
  {
    path: '/treasury/beneficiary-changes',
    name: 'treasury-beneficiary-changes',
    component: () => import('@/modules/treasury/beneficiaries/BeneficiaryChangesView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.beneficiaryChanges',
      permission: TREASURY_PERMISSIONS.beneficiaryRead,
    },
  },
  {
    path: '/treasury/beneficiaries/:id(\\d+)',
    name: 'treasury-beneficiary',
    component: () => import('@/modules/treasury/beneficiaries/BeneficiaryView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.beneficiary',
      permission: TREASURY_PERMISSIONS.beneficiaryRead,
    },
  },

  {
    path: '/treasury/mandates',
    name: 'treasury-mandates',
    component: () => import('@/modules/treasury/mandates/MandatesListView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.mandates',
      permission: TREASURY_PERMISSIONS.mandateRead,
    },
  },
  {
    path: '/treasury/mandates/:id(\\d+)',
    name: 'treasury-mandate',
    component: () => import('@/modules/treasury/mandates/MandateView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.mandate',
      permission: TREASURY_PERMISSIONS.mandateRead,
    },
  },

  {
    path: '/treasury/wallets',
    name: 'treasury-wallets',
    component: () => import('@/modules/treasury/wallets/WalletsListView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.wallets',
      permission: TREASURY_PERMISSIONS.walletRead,
    },
  },
  {
    path: '/treasury/wallet-approvals',
    name: 'treasury-wallet-approvals',
    component: () => import('@/modules/treasury/wallets/WalletApprovalsView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.walletApprovals',
      permission: TREASURY_PERMISSIONS.walletApprove,
    },
  },
  {
    path: '/treasury/wallets/:id(\\d+)',
    name: 'treasury-wallet',
    component: () => import('@/modules/treasury/wallets/WalletView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.wallet',
      permission: TREASURY_PERMISSIONS.walletRead,
    },
  },
  {
    path: '/treasury/wallet-counts/:id(\\d+)',
    name: 'treasury-wallet-count',
    component: () => import('@/modules/treasury/wallets/WalletCountSheetView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.walletCount',
      permission: TREASURY_PERMISSIONS.walletCount,
    },
  },
  {
    path: '/treasury/reports/wallet-balances',
    name: 'treasury-wallet-balances',
    component: () => import('@/modules/treasury/wallets/WalletBalancesReportView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.walletBalances',
      permission: TREASURY_PERMISSIONS.walletRead,
    },
  },

  {
    path: '/treasury/facilities',
    name: 'treasury-facilities',
    component: () => import('@/modules/treasury/debt/FacilitiesListView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.facilities',
      permission: TREASURY_PERMISSIONS.debtRead,
    },
  },
  {
    path: '/treasury/facilities/:id(\\d+)',
    name: 'treasury-facility',
    component: () => import('@/modules/treasury/debt/FacilityView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.facility',
      permission: TREASURY_PERMISSIONS.debtRead,
    },
  },
  {
    path: '/treasury/covenants',
    name: 'treasury-covenants',
    component: () => import('@/modules/treasury/debt/CovenantBoardView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.covenants',
      permission: TREASURY_PERMISSIONS.debtRead,
    },
  },
  {
    path: '/treasury/reports/debt-maturity',
    name: 'treasury-debt-maturity',
    component: () => import('@/modules/treasury/debt/DebtMaturityReportView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.debtMaturity',
      permission: TREASURY_PERMISSIONS.debtRead,
    },
  },

  {
    path: '/treasury/forecasts',
    name: 'treasury-forecasts',
    component: () => import('@/modules/treasury/forecast/ForecastRunsView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.forecasts',
      permission: TREASURY_PERMISSIONS.forecastRead,
    },
  },
  {
    path: '/treasury/forecast-scenarios',
    name: 'treasury-scenarios',
    component: () => import('@/modules/treasury/forecast/ScenariosView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.scenarios',
      permission: TREASURY_PERMISSIONS.forecastRead,
    },
  },
  {
    path: '/treasury/forecasts/:id(\\d+)',
    name: 'treasury-forecast',
    component: () => import('@/modules/treasury/forecast/ForecastRunView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.forecast',
      permission: TREASURY_PERMISSIONS.forecastRead,
    },
  },
  {
    path: '/treasury/reports/forecast-accuracy',
    name: 'treasury-forecast-accuracy',
    component: () => import('@/modules/treasury/forecast/ForecastAccuracyView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.forecastAccuracy',
      permission: TREASURY_PERMISSIONS.forecastRead,
    },
  },

  {
    path: '/treasury/settings',
    name: 'treasury-settings',
    component: () => import('@/modules/treasury/settings/TreasurySettingsView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.settings',
      permission: TREASURY_PERMISSIONS.settingsRead,
    },
  },
]

/* ------------------------------------------------------------ own screens */

export const TREASURY_PATHS = {
  positionPreview: '/treasury/position',
  positions: '/treasury/positions',
  position: (id: number) => `/treasury/positions/${id}`,
  positionDiff: (id: number) => `/treasury/positions/${id}/diff`,
  beneficiaries: '/treasury/beneficiaries',
  beneficiary: (id: number) => `/treasury/beneficiaries/${id}`,
  beneficiaryChanges: '/treasury/beneficiary-changes',
  mandates: '/treasury/mandates',
  mandate: (id: number) => `/treasury/mandates/${id}`,
  wallets: '/treasury/wallets',
  wallet: (id: number) => `/treasury/wallets/${id}`,
  walletApprovals: '/treasury/wallet-approvals',
  walletCount: (id: number) => `/treasury/wallet-counts/${id}`,
  walletBalances: '/treasury/reports/wallet-balances',
  facilities: '/treasury/facilities',
  facility: (id: number) => `/treasury/facilities/${id}`,
  covenants: '/treasury/covenants',
  debtMaturity: '/treasury/reports/debt-maturity',
  forecasts: '/treasury/forecasts',
  forecast: (id: number) => `/treasury/forecasts/${id}`,
  scenarios: '/treasury/forecast-scenarios',
  forecastAccuracy: '/treasury/reports/forecast-accuracy',
  settings: '/treasury/settings',
} as const

/** The wallets one custodian holds — the hop from a person to their floats. */
export function walletsForCustodian(userId: number): string {
  return `${TREASURY_PATHS.wallets}?custodianUserId=${userId}`
}

/* -------------------------------------------------------- links elsewhere */

/** A posted journal entry, from an accrual, a variance or a drawdown. */
export function journalRoute(entryId: number): string {
  return `/gl/journal-entries/${entryId}`
}

/**
 * The general ledger for one account, up to a date — the hop from a published
 * balance to the postings that produced it. The window travels with the link so
 * the page it opens adds up to the figure that was clicked.
 */
export function ledgerRoute(companyId: number, accountId: number, asOf: string): string {
  const params = new URLSearchParams({
    companyId: String(companyId),
    accountId: String(accountId),
  })
  if (asOf !== '') params.set('to', asOf)
  return `/gl/reports/ledger?${params.toString()}`
}

/** A governed transfer instruction, from a replenishment or a drawdown. */
export function instructionRoute(id: number): string {
  return `/cash/transfer-instructions/${id}`
}

/**
 * The path, but only if this app actually has a screen for it.
 *
 * The router's catch-all matches everything, so "resolves" is not the question —
 * "resolves to something other than the not-found page" is. A path the router
 * would send to the 404 screen comes back as null and the caller renders text.
 */
export function linkIfRoutable(router: Router, path: string | null): string | null {
  if (path === null || path === '') return null
  try {
    const resolved = router.resolve(path)
    if (resolved.matched.length === 0) return null
    if (resolved.matched.every((record) => record.name === 'not-found')) return null
    return path
  } catch {
    // An unparseable path is not a link either.
    return null
  }
}
